import type { CSSProperties, ElementType, ReactNode } from 'react';
import type { TokenName } from '../../tokens/tokens';
import styles from './Stack.module.css';

/**
 * The spacing scale, derived from the generated tokens so it can never drift
 * from Figma. Steps are named by their pixel value: `'8'` is 8px.
 */
export type SpacingToken = TokenName extends infer T
  ? T extends `space-${infer K}`
    ? K
    : never
  : never;

export type StackProps = {
  as?: ElementType;
  direction?: 'row' | 'column';
  gap?: SpacingToken;
  padding?: SpacingToken;
  paddingX?: SpacingToken;
  paddingY?: SpacingToken;
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  justify?: 'start' | 'center' | 'end' | 'between';
  /** Stretch to the full width of the parent. */
  fullWidth?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

const alignMap = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
} as const;

const justifyMap = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
} as const;

const space = (t: SpacingToken | undefined) => (t === undefined ? undefined : `var(--space-${t})`);

/**
 * Figma auto-layout, expressed in CSS. Gap and padding accept spacing token
 * names rather than raw lengths, so a Stack can never introduce a hardcoded
 * value. This is the layout building block the sidebar is assembled from, and
 * the one drawers, forms and chat layouts should reuse.
 */
export function Stack({
  as: Component = 'div',
  direction = 'column',
  gap,
  padding,
  paddingX,
  paddingY,
  align,
  justify,
  fullWidth = false,
  className,
  style,
  children,
}: StackProps) {
  // Every property is emitted explicitly, never left undefined. Custom properties
  // inherit, so an omitted value would silently pick up the enclosing Stack's
  // padding or gap — which is exactly the kind of bug this primitive should not
  // have.
  const vars = {
    '--stack-direction': direction,
    '--stack-gap': space(gap) ?? '0',
    '--stack-padding-x': space(paddingX) ?? space(padding) ?? '0',
    '--stack-padding-y': space(paddingY) ?? space(padding) ?? '0',
    '--stack-align': align ? alignMap[align] : 'stretch',
    '--stack-justify': justify ? justifyMap[justify] : 'flex-start',
    ...style,
  } as CSSProperties;

  const classes = [styles.stack, fullWidth ? styles.fullWidth : null, className].filter(Boolean).join(' ');

  return (
    <Component className={classes} style={vars}>
      {children}
    </Component>
  );
}
